import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { ISalesforceOrderCreateResult, ISalesforceOrderDAO, ISalesforceOrderDTO } from '../../../../../api/salesforce-order.dao';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { formatTimezoneOffset, getTimezoneOffsetInMinutes } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../../../../infrastructure/date-formatter';
import { timezonesCodeLabelMap } from '../../../../../infrastructure/timezones-code-label-map';
import { ISubWindowFacade, ISubWindowsService } from '../../../../../infrastructure/sub-windows';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { SALESFORCE_ORDER_TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { alertServerWarning } from '../../../../global/modal/alerts-host/alert-templates';
import { SalesforceOrderTrackingMapper } from './salesforce-order-tracking.mapper';
import { AddSalesforceOrderForm } from './add-salesforce-order-form.presentation';
import { AddSalesforceOrderFormViewModel } from './add-salesforce-order-form.viewmodel';
import { ADD_MODAL_TITLE, EDIT_MODAL_TITLE, ORDER_SAVE_WARNING_TITLE } from './salesforce-order.texts';
import { resolveSalesforceProjectManagers } from './salesforce-shared-fields-tracking.mapper';

const EMPTY = '—';


class SalesforceOrderViewModel {
    @mobx.observable
    public isOpen: boolean = false;
    @mobx.observable
    private loading: boolean = false;
    @mobx.observable
    private error: boolean = false;
    @mobx.observable
    private noOrder: boolean = false;
    @mobx.observable
    private noOrderMessage: string | null = null;
    @mobx.observable
    private order: ISalesforceOrderDTO | null = null;
    @mobx.observable
    public event: IEventDetailsDTO;

    constructor(
        event: IEventDetailsDTO,
        private readonly accountId: number,
        @IoC.Inject private readonly salesforceOrderDAO: ISalesforceOrderDAO = IoC.Container.get(ISalesforceOrderDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
        this.event = event;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get hasLinkedOrder(): boolean {
        return !!this.event?.orderId;
    }

    public getEventName(): string {
        return this.event?.name ?? '';
    }

    public getEventDateLines(): string[] {
        const { startDateTime, endDateTime, timeZone } = this.event ?? {};
        if (!startDateTime || !timeZone) {
            return [];
        }
        const formatter = IoC.Container.get(IDateTimeFormatter);
        const format = (date: Date): string =>
            formatter.dateToFormattedString(date, DateFnsFormats.StringifiedDayMonthNumericDateYear, timeZone);
        const start = format(startDateTime);
        const end = endDateTime ? format(endDateTime) : null;
        if (!end || end === start) {
            return [start];
        }
        return [`${start} - ${end}`];
    }

    public getEventTimeRangeLabel(): string {
        const { startDateTime, endDateTime, timeZone } = this.event ?? {};
        if (!startDateTime || !timeZone) {
            return '';
        }
        const formatter = IoC.Container.get(IDateTimeFormatter);
        const toTime = (date: Date): string =>
            formatter.dateToFormattedString(date, TimeFormats.Day12Hours, timeZone)
                .replace('AM', 'am')
                .replace('PM', 'pm');
        return endDateTime ? `${toTime(startDateTime)} - ${toTime(endDateTime)}` : toTime(startDateTime);
    }

    public getEventTimezoneLabel(): string {
        const { timeZone } = this.event ?? {};
        if (!timeZone) {
            return '';
        }
        const offset = `GMT${formatTimezoneOffset(getTimezoneOffsetInMinutes(timeZone))}`;
        return `(${offset}) ${timezonesCodeLabelMap[timeZone] || timeZone}`;
    }

    public getEventWhenLines(): string[] {
        const dateLines = this.getEventDateLines();
        const timeRange = this.getEventTimeRangeLabel();
        const timezone = this.getEventTimezoneLabel();
        const timeLine = [timeRange, timezone].filter(Boolean).join(', ');
        if (dateLines.length === 0) {
            return [timeLine].filter(Boolean);
        }
        return [dateLines[0], timeLine].filter(Boolean);
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public get hasError(): boolean {
        return this.error;
    }

    public get hasNoOrder(): boolean {
        return this.noOrder;
    }

    public getNoOrderMessage(): string | null {
        return this.noOrderMessage;
    }

    public getOrder(): ISalesforceOrderDTO | null {
        return this.order;
    }

    public getOrderId(): string | null {
        return this.event?.orderId ?? null;
    }

    public getOeNumber(): string {
        return this.event?.oeNumber ?? '';
    }

    public getRegion(): string {
        return this.event?.region || EMPTY;
    }

    public getClientDepartment(): string {
        return this.event?.department || EMPTY;
    }

    public getProjectManager(): string {
        const [pm] = resolveSalesforceProjectManagers(this.event?.deliveryTeamMembers);
        if (!pm) {
            return EMPTY;
        }
        const name = pm.fullName || `${pm.firstName ?? ''} ${pm.lastName ?? ''}`.trim();
        return name || EMPTY;
    }

    public getProjectManager2(): string {
        const [, pm2] = resolveSalesforceProjectManagers(this.event?.deliveryTeamMembers);
        if (!pm2) {
            return EMPTY;
        }
        const name = pm2.fullName || `${pm2.firstName ?? ''} ${pm2.lastName ?? ''}`.trim();
        return name || EMPTY;
    }

    public hasProjectManager2(): boolean {
        const [, pm2] = resolveSalesforceProjectManagers(this.event?.deliveryTeamMembers);
        return !!pm2;
    }

    @mobx.action.bound
    public async open(): Promise<void> {
        this.isOpen = true;
        await this.fetchOrder();
    }

    @mobx.action.bound
    public close(): void {
        this.isOpen = false;
    }


    @mobx.action.bound
    public openAddOrder(): void {
        let subWindow: ISubWindowFacade | null = null;
        const formVm = new AddSalesforceOrderFormViewModel(this.event, this.accountId, result => {
            this.linkCreatedOrder(result);
            subWindow?.close();
            void this.open().then(() => this.showSaveWarningIfAny(result));
        });
        subWindow = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(ADD_MODAL_TITLE),
            subWindowClassname: 'add-salesforce-order-subwindow',
            componentToHost: AddSalesforceOrderForm,
            componentToHostProps: { vm: formVm },
            // Block the cross-close while a create is in flight (wait state); close normally otherwise.
            onCloseIconClickHandler: () => {
                if (!formVm.isSaving) {
                    subWindow?.close();
                }
            },
        });
    }

    @mobx.action.bound
    public openEditOrder(): void {
        const order = this.order;
        if (!order) {
            return;
        }
        let subWindow: ISubWindowFacade | null = null;
        const formVm = new AddSalesforceOrderFormViewModel(
            this.event,
            this.accountId,
            result => {
                subWindow?.close();
                void this.open().then(() => this.showSaveWarningIfAny(result));
            },
            'edit',
            order,
        );
        subWindow = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(EDIT_MODAL_TITLE, { orderNumber: this.getOeNumber() }),
            subWindowClassname: 'add-salesforce-order-subwindow',
            componentToHost: AddSalesforceOrderForm,
            componentToHostProps: { vm: formVm },
            onCloseIconClickHandler: () => {
                if (!formVm.isSaving) {
                    subWindow?.close();
                }
            },
        });
        this.close();
    }

    private showSaveWarningIfAny(result: ISalesforceOrderCreateResult): void {
        if (result.warningMessage) {
            alertServerWarning(this.intl.formatMessage(ORDER_SAVE_WARNING_TITLE), result.warningMessage);
        }
    }

    @mobx.action
    private linkCreatedOrder(result: ISalesforceOrderCreateResult): void {
        if (!result.orderId) {
            return;
        }
        this.event = { ...this.event, orderId: result.orderId, oeNumber: result.orderNumber ?? this.event.oeNumber };
    }

    @mobx.action
    private async fetchOrder(): Promise<void> {
        this.loading = true;
        this.error = false;
        this.noOrder = false;
        this.noOrderMessage = null;
        try {
            const order = await this.salesforceOrderDAO.getOrderForEvent({
                accountId: this.accountId,
                eventId: this.event.eventId,
                orderId: this.event.orderId,
            });
            if (order && order.available && order.orderLinked && order.order) {
                this.setOrder(order);
                this.trackView(order);
                return;
            }
            this.setNoOrder(order?.message ?? null);
        } catch (e) {
            this.setError();
        } finally {
            this.stopLoading();
        }
    }

    @mobx.action
    private setOrder(order: ISalesforceOrderDTO): void {
        this.order = order;
    }

    @mobx.action
    private setNoOrder(message: string | null): void {
        this.noOrder = true;
        this.noOrderMessage = message;
        this.order = null;
    }

    @mobx.action
    private setError(): void {
        this.error = true;
        this.order = null;
    }

    @mobx.action
    private stopLoading(): void {
        this.loading = false;
    }

    private trackView(order: ISalesforceOrderDTO): void {
        this.trackingService.get().track(
            SALESFORCE_ORDER_TRACKING_EVENTS.VIEW_SALESFORCE_ORDER,
            SalesforceOrderTrackingMapper.toTrackingEvent(this.event, order),
        );
    }
}

export { SalesforceOrderViewModel };
